import { useCallback, useMemo, useRef, useState } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import { Loadable } from "jotai/vanilla/utils/loadable";
import {
  type Key,
  Text,
  Typeahead,
  TypeaheadItem,
} from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";
import {
  takeSnapshotSetter,
  Zap,
  zapsSearchAsyncAtomFamily,
  closeSecondaryToolbarMenuSetter,
} from "@zapier/canvas-state";

import { useEmitResourceConnectEvents } from "../../../hooks/useEmitResourceConnectEvents";
import useUpdateNode from "../../../hooks/useUpdateNode";
import { useFocusInput } from "../../../hooks/useFocusInput";
import { useDebouncedValue } from "../../../hooks/useDebouncedValue";
import { CreateNewZapButton } from "../../CreateNewZapButton";
import { NewAssetButtonWrapper } from "../NewAssetButtonWrapper";
import { SelectedItem } from "../SelectedItem";
import { NodeToolbarBottom } from "../NodeToolbarBottom";
import { MenuWrapper } from "../MenuWrapper";

import styles from "./FloatingZapsTypeahead.module.css";

type Props = {
  hasZap?: boolean;
  isZapNotFound?: boolean;
  zapTitle: Zap["title"] | undefined;
};

export function FloatingZapsTypeahead({
  hasZap,
  zapTitle,
  isZapNotFound,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const takeSnapshot = useSetAtom(takeSnapshotSetter);
  const updateNodeAndSave = useUpdateNode();
  const { emitEventOnZapConnect } = useEmitResourceConnectEvents();

  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);
  const closeSecondaryMenu = useSetAtom(closeSecondaryToolbarMenuSetter);

  const zapsLoadable = useAtomValue(
    zapsSearchAsyncAtomFamily({
      searchTerm: debouncedSearchTerm,
      showAllWhenEmpty: false,
    }),
  ) as Loadable<Zap[]>;
  const isLoading = zapsLoadable.state === "loading";
  const hasData = zapsLoadable.state === "hasData";
  const zaps = useMemo(
    () => (hasData ? zapsLoadable.data : []),
    [hasData, zapsLoadable],
  );

  useFocusInput(inputRef);

  const onInputChange = useCallback(
    (value: string) => setSearchTerm(value),
    [setSearchTerm],
  );

  const onSelectionChange = useCallback(
    (key: Key | null) => {
      if (key) {
        takeSnapshot();

        updateNodeAndSave({
          type: "zap",
          data: {
            zapId: key,
            isExpanded: false,
          },
        });

        emitEventOnZapConnect(String(key));

        setSearchTerm("");
        closeSecondaryMenu();
      }
    },
    [
      takeSnapshot,
      updateNodeAndSave,
      emitEventOnZapConnect,
      closeSecondaryMenu,
    ],
  );

  const onRemoveZap = useCallback(() => {
    takeSnapshot();

    updateNodeAndSave({
      data: {
        zapId: undefined,
        isExpanded: undefined,
      },
    });
  }, [updateNodeAndSave, takeSnapshot]);

  return (
    <NodeToolbarBottom offset={15}>
      <MenuWrapper>
        {hasZap ? (
          <SelectedItem
            label={isZapNotFound ? "Zap not found" : zapTitle || "Untitled Zap"}
            icon={<Icon name="miscBoltAltFill" size={26} color="BrandOrange" />}
            clearSelection={onRemoveZap}
          />
        ) : (
          <>
            <div className={styles.floatingTypeaheadWrapper}>
              <NewAssetButtonWrapper>
                <CreateNewZapButton />
              </NewAssetButtonWrapper>
              <Typeahead
                aria-label="Search Zap workflows"
                placeholder="Search Zap workflows"
                ref={inputRef}
                onInputChange={onInputChange}
                onChange={onSelectionChange}
                isBusy={isLoading}
                classNamePopOver={styles.popover}
                size="medium"
              >
                {zaps.map((zap) => {
                  return (
                    <TypeaheadItem
                      key={zap.id}
                      id={zap.id}
                      textValue={zap.title}
                      className={styles.floatingTypeaheadItem}
                      size="medium"
                    >
                      <Icon name="miscBoltAlt" size={22} color="BrandOrange" />
                      <div className={styles.truncateText}>
                        <Text type="SmallPrint2">
                          {zap.title ?? "Untitled Zap"}
                        </Text>
                      </div>
                    </TypeaheadItem>
                  );
                })}
              </Typeahead>
            </div>
          </>
        )}
      </MenuWrapper>
    </NodeToolbarBottom>
  );
}
